import { formatDateNumber } from '@seen/core/dates'
import {
  ExtendedMedicationRequest,
  type MedicationGroup,
  medicationLabelForGroup,
  type MedicationState,
} from '@seen/core/models/medicationRequest'
import {
  Badge,
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  cn,
  TypographySubtle,
} from '@seen/ui'
import {
  IconAlertTriangle,
  IconClockExclamation,
  IconInfoCircle,
  IconReload,
  IconSignature,
} from '@tabler/icons-react'
import type { JSX } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#src/components/core/index.ts'
import {
  MEDICATION_BADGE_CLASS,
  MedicationStateBadge,
  MedicationTypeBadge,
} from '#src/components/medications/MedicationBadges.tsx'
import { MedicationName } from '#src/components/medications/MedicationName.tsx'
import { getMedicationThreadId } from '#src/hooks/useMedicationDraftsByThread.ts'

/* Helper: Format status reason for stopped/cancelled/completed medications */
function formatStatusReason(medicationState: MedicationState): string | null {
  if (
    medicationState.category === 'stopped' ||
    medicationState.category === 'cancelled' ||
    medicationState.category === 'completed'
  ) {
    return `Reason: ${medicationState.description}`
  }

  return null
}

type DraftOrderType = 'Refill'

/* Refill eligibility is checked via ExtendedMedicationRequest.refillUnavailableReason */

interface MedicationRequestGroupedListProps {
  groups: MedicationGroup[]
  onGroupClick?: (group: MedicationGroup, requestId?: string) => void
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: DraftOrderType
  ) => void
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
  className?: string
}

/*
 * Display component for grouped medication requests.
 * Groups multiple requests for the same medication and shows current status,
 * pharmacy errors, and indicates when there's history to review.
 */
export function MedicationRequestGroupedList({
  groups,
  onGroupClick,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
  className,
}: MedicationRequestGroupedListProps): JSX.Element {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <TypographySubtle>No medications found</TypographySubtle>
      </div>
    )
  }

  return (
    <div className={cn('divide-y divide-border', className)}>
      {groups.map((group) => (
        <MedicationGroupRow
          key={group.requests[0]?.id ?? group.medicationName}
          group={group}
          onGroupClick={onGroupClick}
          onDraftPrescription={onDraftPrescription}
          onOpenDraftPrescription={onOpenDraftPrescription}
          draftIdsByThreadId={draftIdsByThreadId}
          draftingMedicationIds={draftingMedicationIds}
        />
      ))}
    </div>
  )
}

interface MedicationGroupRowProps {
  group: MedicationGroup
  onGroupClick?: (group: MedicationGroup, requestId?: string) => void
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: DraftOrderType
  ) => void
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
}

function MedicationGroupRow({
  group,
  onGroupClick,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
}: MedicationGroupRowProps): JSX.Element {
  const actionRequest = group.representativeRequest
  const rowLabel = medicationLabelForGroup(group)
  const isClickable = onGroupClick !== undefined
  const quantity = actionRequest.dispenseRequest?.quantity
  const sig = actionRequest.dosageInstructions?.[0]
  const badgeState = ExtendedMedicationRequest.classifyMedicationState([
    actionRequest.toFhirResource(),
  ])
  const issues = ExtendedMedicationRequest.detectIssues([
    actionRequest.toFhirResource(),
  ])
  const statusReason = formatStatusReason(badgeState)
  const date = actionRequest.effectiveDate
  const isElationSourced = !!actionRequest.externalResourceJSON
  const refillReason = actionRequest.refillUnavailableReason
  const threadId = getMedicationThreadId(actionRequest)
  const draftId = threadId ? draftIdsByThreadId?.get(threadId) : undefined
  const isCreatingDraft = draftingMedicationIds?.has(actionRequest.id) ?? false
  const canSignDraft =
    onOpenDraftPrescription !== undefined && draftId !== undefined
  const canDraft = onDraftPrescription && isElationSourced && !refillReason

  const rowContent = (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 transition-colors',
        isClickable && 'cursor-pointer hover:bg-muted/50'
      )}
      onClick={
        isClickable ? () => onGroupClick?.(group, actionRequest.id) : undefined
      }
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onGroupClick?.(group, actionRequest.id)
        }
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {/* Main content area */}
      <div className="min-w-0 flex-1">
        {/* First line: Name + Status + Rx/OTC + Date */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="shrink truncate text-sm font-medium">
              <MedicationName
                display={rowLabel.display}
                doseSummary={rowLabel.doseSummary}
              />
            </span>
            <StateBadgeWithTooltip state={badgeState} />
            <MedicationTypeBadge type={actionRequest.type} />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canSignDraft && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenDraftPrescription(draftId, actionRequest)
                    }}
                  >
                    <IconSignature />
                    Sign
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open draft in Elation</TooltipContent>
              </Tooltip>
            )}
            {!canSignDraft && onDraftPrescription && isElationSourced && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="ghost"
                      size="xs"
                      className="text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
                      loading={isCreatingDraft}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (canDraft) {
                          onDraftPrescription(actionRequest, 'Refill')
                        }
                      }}
                      disabled={!!refillReason || isCreatingDraft}
                    >
                      <IconReload />
                      Refill
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {refillReason ?? 'Create refill draft in Elation'}
                </TooltipContent>
              </Tooltip>
            )}
            {date && (
              <span className="text-xs text-muted-foreground">
                {formatDateNumber(date)}
              </span>
            )}
          </div>
        </div>

        {/* Second line: Sig + Qty + Supply/Reason */}
        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
          {sig && <span className="truncate">{sig}</span>}
          {quantity?.value && (
            <>
              {sig && <span className="shrink-0">•</span>}
              <span className="shrink-0">
                Qty: {quantity.value}
                {quantity.unit && ` ${quantity.unit}`}
              </span>
            </>
          )}
          {statusReason && (
            <>
              <span className="shrink-0">•</span>
              <span className="truncate">{statusReason}</span>
            </>
          )}
        </div>
      </div>

      {/* Issues on the right */}
      {issues.length > 0 && (
        <div className="flex shrink-0 gap-1">
          {issues.map((issue, idx) => (
            <IssueBadge key={`${issue.type}-${idx}`} issue={issue} />
          ))}
        </div>
      )}
    </div>
  )

  const showSignContextAction = canSignDraft
  const showRefillContextAction = !showSignContextAction && canDraft

  if (!showSignContextAction && !showRefillContextAction) {
    return rowContent
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{rowContent}</ContextMenuTrigger>
      <ContextMenuContent>
        {showSignContextAction && draftId !== undefined && (
          <ContextMenuItem
            onClick={() => onOpenDraftPrescription?.(draftId, actionRequest)}
          >
            <IconSignature className="size-4" />
            Sign
          </ContextMenuItem>
        )}
        {showRefillContextAction && (
          <ContextMenuItem
            onClick={() => onDraftPrescription?.(actionRequest, 'Refill')}
            disabled={isCreatingDraft}
          >
            <IconReload className="size-4" />
            Refill
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}

function StateBadgeWithTooltip({
  state,
}: {
  state: MedicationGroup['medicationState']
}): JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <MedicationStateBadge state={state} emphasizeAction />
      </TooltipTrigger>
      <TooltipContent>
        <div className="max-w-xs text-xs">
          <p>{state.description}</p>
          {state.actionReason && (
            <p className="mt-1 font-semibold text-orange-400">
              {state.actionReason}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function IssueBadge({
  issue,
}: {
  issue: MedicationGroup['issues'][number]
}): JSX.Element {
  const severityColorMap: Record<typeof issue.severity, string> = {
    error: 'red',
    warning: 'orange',
    info: 'blue',
  }

  const severityIconMap: Record<typeof issue.severity, JSX.Element> = {
    error: <IconAlertTriangle className="size-3" />,
    warning: <IconClockExclamation className="size-3" />,
    info: <IconInfoCircle className="size-3" />,
  }

  return (
    <Tooltip>
      <TooltipTrigger>
        <Badge
          variant="outline"
          color={severityColorMap[issue.severity] as any}
          className={cn(MEDICATION_BADGE_CLASS, 'gap-1')}
        >
          {severityIconMap[issue.severity]}
          <span>{issue.type.replace(/-/g, ' ')}</span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="max-w-xs text-xs">
          <p className="mb-1 font-semibold capitalize">
            {issue.severity}: {issue.type.replace(/-/g, ' ')}
          </p>
          <p>{issue.message}</p>
          <p className="mt-1 text-muted-foreground">
            Click to view details and resolve
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
